import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useVisibleSections } from './useVisibleSections';

type ContextValue = ReturnType<typeof useVisibleSections>;

export const VisibleSectionsContext = createContext<ContextValue | undefined>(
  undefined
);

interface Props {
  children?: ReactNode;
}

/**
 * Handles company report visible sections
 */
export function VisibleSectionsProvider({ children }: Props) {
  const {
    visibleSections,
    nextSectionLabel,
    previousSectionLabel,
    loadNextSection,
    loadPreviousSection,
    loadEverySection,
    hasNextSection,
    hasPreviousSection,
    loadSection,
    handleSectionOnMount,
    scrollToSection,
    isPristine,
    getSectionLabel,
  } = useVisibleSections();

  const value = useMemo(
    () => ({
      visibleSections,
      nextSectionLabel,
      previousSectionLabel,
      loadNextSection,
      loadPreviousSection,
      loadEverySection,
      hasNextSection,
      hasPreviousSection,
      loadSection,
      handleSectionOnMount,
      scrollToSection,
      isPristine,
      getSectionLabel,
    }),
    [
      visibleSections,
      nextSectionLabel,
      previousSectionLabel,
      loadNextSection,
      loadPreviousSection,
      loadEverySection,
      hasNextSection,
      hasPreviousSection,
      loadSection,
      handleSectionOnMount,
      scrollToSection,
      isPristine,
      getSectionLabel,
    ]
  );
  return (
    <VisibleSectionsContext.Provider value={value}>
      {children}
    </VisibleSectionsContext.Provider>
  );
}

export function useVisibleSectionsContext() {
  const context = useContext(VisibleSectionsContext);
  const defaultReturn = useVisibleSections();
  if (typeof context === 'undefined') {
    return defaultReturn;
  }
  return context;
}
